import { CalendarStateFilter } from '$src/js/modules/calendar/state/calendarState.svelte';
import type { R2REvent, R2REvents } from '$src/types/events';
import Fuse from 'fuse.js';
import { MapboxService } from '$src/js/modules/mapbox/MapboxService';

export class CalendarService {
  protected MAPBOX_TOKEN =
    'pk.eyJ1IjoicmVhZHkycmFjZSIsImEiOiJjbTd2dWxoeXEwMDhnMnFyMDVjcXN2azFsIn0.OWaYQK_O4Rl87WyHQ1OM7g';

  private LOCATION_PREVIOUS_SEARCH = '';

  private LOCATION_PREVIOUS_OUTPUT: [number, number] | [] = [];

  public FILTER_DISTANCE_OPTIONS = {
    10000: '10km',
    20000: '20km',
    30000: '30km',
    50000: '50km',
    100000: '100km',
    150000: '150km',
    999999: 'Meer dan 150km',
  };

  /**
   * Match tags. If "" (first), return true.
   * @param tags
   * @param event
   * @returns
   */
  private matchTags(tags: string[], event: R2REvent) {
    if (tags[0] === '') {
      return true;
    }

    return tags.some((tag) => event.type.slug === tag);
  }

  /**
   * Search string in event title.
   * Use fuzzy serach by Fuse
   * @param searchString
   * @param event
   * @returns
   */
  private matchSearch(searchString: string, event: R2REvent) {
    if (!searchString) {
      return false;
    }

    const fuse = new Fuse([event], {
      keys: ['title'],
      threshold: 0.3,
      ignoreLocation: true,
    });

    const results = fuse.search(searchString);

    return results.length > 0;
  }

  /**
   * Get distance between two coordinates.
   * Used the Haversine formula.
   * @param lat1
   * @param lon1
   * @param lat2
   * @param lon2
   * @returns
   */
  private getDistanceBetweenCoordinates(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const earthRadius = 6371000;
    const toRadians = (degrees: number): number => degrees * (Math.PI / 180);

    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);

    // Haversine formule
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRadians(lat1)) *
        Math.cos(toRadians(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = Math.round(earthRadius * c);

    return distance;
  }

  /**
   * Match location base don given locationString and distanceString.
   * @param locationString
   * @param distanceString
   * @param event
   * @returns
   */
  private matchLocation(locationString: number[] | [], distanceString: string, event: R2REvent) {
    const lat = locationString[1];
    const lng = locationString[0];

    if (!lat || !lng) {
      return false;
    }

    const distance = parseInt(distanceString, 10);
    const eventLocationLat = event.location.lat;
    const eventLocationLng = event.location.lng;

    const distanceBetween = this.getDistanceBetweenCoordinates(
      lat,
      lng,
      eventLocationLat,
      eventLocationLng
    );

    return distanceBetween <= distance;
  }

  /**
   * Use two dates and check if events are between those dates.
   * @param dateString
   * @param event
   * @returns
   */
  private matchDate(dateString: Date[] | undefined, event: R2REvent) {
    const startDate = dateString?.[0];
    const endDate = dateString?.[1];

    if (!startDate || !endDate) {
      return false;
    }

    const eventDate = new Date(event.date.date);

    return eventDate >= startDate && eventDate <= endDate;
  }

  async getGeoLocationFromString(string: string): Promise<[number, number] | []> {
    if (!string) {
      return [];
    }

    if (string === this.LOCATION_PREVIOUS_SEARCH) {
      return this.LOCATION_PREVIOUS_OUTPUT;
    }

    try {
      const mapboxService = new MapboxService();
      const geoResponse = await mapboxService.getGeoLocationFromString(string);
      const [lng, lat] = [geoResponse.lng, geoResponse.lat];

      if (typeof lng !== 'number' || typeof lat !== 'number') {
        return [];
      }

      const validCoordinates: [number, number] = [lng, lat];

      this.LOCATION_PREVIOUS_SEARCH = string;
      this.LOCATION_PREVIOUS_OUTPUT = validCoordinates;

      return validCoordinates;
    } catch (error) {
      console.error('Error fetching location:', error);
      return [];
    }
  }

  /**
   * Filter events based on active filters.
   * No active filters? Show all window.events
   *
   * @param events
   * @returns
   */
  filter(events?: R2REvents) {
    if (!events) {
      return window.events;
    }

    const activeFilters = {
      tags: !!CalendarStateFilter.tags.length,
      search: !!CalendarStateFilter.search,
      location:
        !!CalendarStateFilter.locationCoordinates[0] &&
        !!CalendarStateFilter.locationCoordinates[1],
      date: !!CalendarStateFilter.dateObjects[0] && !!CalendarStateFilter.dateObjects[1],
    };

    const filtersActiveLength = Object.values(activeFilters).filter(Boolean).length;

    if (!filtersActiveLength) {
      return events;
    }

    return events.filter((event: R2REvent) => {
      let score = 0;

      if (this.matchTags(CalendarStateFilter.tags, event)) {
        score += 1;
      }

      if (this.matchSearch(CalendarStateFilter.search, event)) {
        score += 1;
      }

      if (
        this.matchLocation(
          CalendarStateFilter.locationCoordinates,
          CalendarStateFilter.distance,
          event
        )
      ) {
        score += 1;
      }

      if (this.matchDate(CalendarStateFilter.dateObjects, event)) {
        score += 1;
      }

      return score === Object.values(activeFilters).filter(Boolean).length;
    });
  }
}
